// src/components/ui/ScrollScrubVideoFX.jsx
import { useEffect, useRef } from 'react';
import { useGsapContext } from '../../hooks/useGsapContext';
import { gsap, ScrollTrigger } from '../../lib/gsap/setupGsap';

export default function ScrollScrubVideoFX({
  sources = [],
  poster = '',
  className = '',
  range = [0, 1],
  minHeightMobile = '36vh',
  fit = 'cover',
  aspect = '1',
  ariaLabel = 'Vídeo controlado por scroll',
  start = 'top bottom',
  end   = 'bottom top',
  scrub = true,
  pivotProgress = 0.5,
}) {
  const { ref, ctx } = useGsapContext();
  const videoRef = useRef(null);

  useEffect(() => {
    const wrap = ref.current;
    const v = videoRef.current;
    if (!wrap || !v) return;

    let duration = 0;

    const onMeta = () => {
      duration = v.duration || 0;
      v.pause();
      v.muted = true;
      v.playbackRate = 0;

      ctx.current.add(() => {
        const clamp01 = (x) => Math.max(0, Math.min(1, x));
        const sF = clamp01(range[0]);
        const eF = clamp01(range[1]);

        const startSec = sF * duration;
        const endSec   = eF * duration || 0;
        const span     = Math.max(0, endSec - startSec);

        v.currentTime = endSec; // quieto en el final

        let prevP = null;

        const setTime = (t) => {
          if (Number.isFinite(t)) {
            if (typeof v.fastSeek === 'function') {
              try { v.fastSeek(t); return; } catch {}
            }
            v.currentTime = t;
          }
        };

        const st = ScrollTrigger.create({
          trigger: wrap,
          start,
          end,
          scrub,
          // markers: true,
          onUpdate: (self) => {
            const p = clamp01(self.progress);
            const pivot = clamp01(pivotProgress);

            if (p <= pivot) {
              if (v.currentTime !== endSec) setTime(endSec);
              prevP = p;
              return;
            }

            const goingDown = prevP == null ? true : p > prevP;

            // progresión cruda dentro del subtramo (pivot..end): 0..1
            const segRaw = (p - pivot) / (1 - pivot);

            let t;
            if (goingDown) {
              // Reversa: end -> start
              const segDown = segRaw;               // 0→1
              t = endSec - segDown * span;
            } else {
              // Normal: start -> end (invertimos para que al subir avance)
              const segUp = 1 - segRaw;             // 1→0 => 0→1
              t = startSec + segUp * span;
            }

            setTime(t);
            prevP = p;
          },
          onRefresh: (self) => {
            const p = clamp01(self.progress);
            const pivot = clamp01(pivotProgress);
            prevP = p;

            if (p <= pivot) {
              setTime(endSec);
            } else {
              const segRaw = (p - pivot) / (1 - pivot);
              // al refrescar dejamos mapeo "normal" para evitar saltos
              const segUp = 1 - segRaw;
              setTime(startSec + segUp * span);
            }
          },
        });

        ScrollTrigger.refresh();
        return () => st.kill();
      });
    };

    if (v.readyState >= 1) onMeta();
    else v.addEventListener('loadedmetadata', onMeta, { once: true });

    return () => v && v.removeEventListener('loadedmetadata', onMeta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, start, end, scrub, pivotProgress]);

  return (
    <div
      ref={ref}
      className={`ssv ${fit === 'cover' ? 'ssv--cover' : ''} ${className}`}
      style={{ '--ssv-min-h': minHeightMobile, '--ssv-aspect': aspect }}
      aria-label={ariaLabel}
    >
      <div className="ssv__frame">
        <video
          ref={videoRef}
          className="ssv__video"
          poster={poster}
          preload="auto"
          playsInline
          muted
          controls={false}
          controlsList="nodownload nofullscreen noplaybackrate"
          disablePictureInPicture
        >
          {sources.map((s, i) => (
            <source key={i} src={s.src} type={s.type} />
          ))}
        </video>
      </div>
    </div>
  );
}
